/**
 * Remove dangerous tags and attributes from HTML strings before using dangerouslySetInnerHTML
 * This helps prevent XSS attacks by sanitizing potentially malicious content
 */
export const removeDangereous = (htmlString: string): string => {
  if (!htmlString) {
    return '';
  }

  // List of dangerous tags that should be completely removed
  const dangerousTags = [
    'script',
    'iframe',
    'object',
    'embed',
    'form',
    'input',
    'textarea',
    'button',
    'select',
    'option',
    'link',
    'meta',
    'style',
    'base',
    'applet',
    'frame',
    'frameset',
    'noscript',
    'xml'
  ];

  // List of dangerous attributes that should be removed
  const dangerousAttributes = [
    'onclick',
    'onload',
    'onunload',
    'onabort',
    'onerror',
    'onblur',
    'onchange',
    'onfocus',
    'onreset',
    'onselect',
    'onsubmit',
    'onkeydown',
    'onkeypress',
    'onkeyup',
    'onmousedown',
    'onmousemove',
    'onmouseout',
    'onmouseover',
    'onmouseup',
    'ondblclick',
    'ondrag',
    'ondragend',
    'ondragenter',
    'ondragleave',
    'ondragover',
    'ondragstart',
    'ondrop',
    'onscroll',
    'onwheel',
    'oncontextmenu',
    'oninput',
    'oninvalid',
    'onshow',
    'ontoggle',
    // eslint-disable-next-line no-script-url
    'javascript:',
    'data:',
    'vbscript:'
  ];

  let sanitized = htmlString;

  // Remove dangerous tags and their content
  dangerousTags.forEach(tag => {
    const regex = new RegExp(`<${tag}[^>]*>.*?</${tag}>`, 'gis');
    sanitized = sanitized.replace(regex, '');

    // Also remove self-closing versions
    const selfClosingRegex = new RegExp(`<${tag}[^>]*/?>`, 'gis');
    sanitized = sanitized.replace(selfClosingRegex, '');
  });

  // Remove dangerous attributes
  dangerousAttributes.forEach(attr => {
    if (attr.includes(':')) {
      // For protocol-based attributes like javascript:, data:
      const regex = new RegExp(`(href|src|action)\\s*=\\s*["']?${attr.replace(':', '\\:')}[^"'\\s>]*["']?`, 'gis');
      sanitized = sanitized.replace(regex, '');
    } else {
      // For event handlers and other dangerous attributes
      const regex = new RegExp(`${attr}\\s*=\\s*["'][^"']*["']`, 'gis');
      sanitized = sanitized.replace(regex, '');
    }
  });

  // Remove any remaining javascript: or data: protocols
  sanitized = sanitized.replace(/(href|src|action)\s*=\s*["']?(javascript|data|vbscript):[^"'\s>]*["']?/gis, '');

  // Clean up any malformed tags that might have been left behind
  sanitized = sanitized.replace(/<[^>]*>/g, (match) => {
    // Only allow basic HTML tags commonly used in SVG and safe HTML
    const allowedTagsRegex = /^<\/?(?:svg|g|path|circle|rect|line|polyline|polygon|ellipse|text|tspan|defs|clipPath|mask|pattern|use|symbol|image|foreignObject|div|span|p|br|hr|strong|b|em|i|u|small|sub|sup|mark|del|ins|q|blockquote|cite|abbr|dfn|time|code|var|samp|kbd|pre|h1|h2|h3|h4|h5|h6|ul|ol|li|dl|dt|dd|table|thead|tbody|tfoot|tr|th|td|caption|colgroup|col|figure|figcaption|main|section|article|aside|header|footer|nav|address)(\s[^>]*)?>$/i;

    if (allowedTagsRegex.test(match)) {
      return match;
    }
    return '';
  });

  return sanitized;
};
